"use client"

import { ImageOff } from "lucide-react"
import { cn } from "@/lib/utils"

/** User-facing copy — no raw provider/storage errors */
export const MEDIA_FALLBACK_COPY = {
  short: "Image unavailable",
  photo: "This photo is no longer available",
  media: "Media unavailable",
} as const

/** Inline / card / grid: neutral surface, stable layout */
export function MediaGridUnavailable({
  className,
  label = MEDIA_FALLBACK_COPY.short,
  iconClassName,
}: {
  className?: string
  label?: string
  iconClassName?: string
}) {
  return (
    <div
      role="img"
      aria-label={label}
      className={cn(
        "flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-b from-muted/80 to-muted px-2 py-4 text-center",
        className,
      )}
    >
      <ImageOff
        className={cn("h-8 w-8 shrink-0 text-muted-foreground/70", iconClassName)}
        strokeWidth={1.25}
        aria-hidden
      />
      <p className="type-caption leading-snug text-muted-foreground">{label}</p>
    </div>
  )
}

/** Hero cover: fills parent; keeps hero frame */
export function MediaCoverUnavailable({
  className,
  label = MEDIA_FALLBACK_COPY.short,
}: {
  className?: string
  label?: string
}) {
  return (
    <div
      className={cn(
        "absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-muted via-muted/90 to-muted/70",
        className,
      )}
      role="img"
      aria-label={label}
    >
      <ImageOff
        className="h-10 w-10 text-muted-foreground/60"
        strokeWidth={1.15}
        aria-hidden
      />
      <p className="type-body-sm text-muted-foreground">{label}</p>
    </div>
  )
}

/** Skeleton while loading — matches parent bounds */
export function MediaAspectSkeleton({
  className,
  shimmer = true,
}: {
  className?: string
  /** Prefer false when `prefers-reduced-motion` handled by parent */
  shimmer?: boolean
}) {
  return (
    <div
      className={cn(
        "absolute inset-0 bg-muted/80",
        shimmer && "motion-safe:animate-pulse motion-reduce:animate-none",
        className,
      )}
      aria-hidden
    />
  )
}

/** Fullscreen viewer on black stage */
export function MediaLightboxUnavailablePanel({
  title = MEDIA_FALLBACK_COPY.photo,
  description,
  onRetry,
  showRetry,
  onRemove,
  showRemove,
  removeBusy,
}: {
  title?: string
  description?: string
  onRetry?: () => void
  showRetry?: boolean
  onRemove?: () => void
  showRemove?: boolean
  removeBusy?: boolean
}) {
  return (
    <div className="flex min-h-[min(50dvh,24rem)] w-full max-w-md flex-col items-center justify-center gap-5 px-6 text-center">
      <div
        className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04]"
        aria-hidden
      >
        <ImageOff className="h-7 w-7 text-zinc-500" strokeWidth={1.2} />
      </div>
      <div className="space-y-1.5">
        <p className="type-body-sm font-medium text-zinc-200">{title}</p>
        {description ? (
          <p className="type-caption text-zinc-500">{description}</p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-3">
        {showRetry && onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="type-body-sm rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 text-zinc-200 transition-colors hover:bg-white/[0.1]"
          >
            Try again
          </button>
        ) : null}
        {showRemove && onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            disabled={removeBusy}
            className="type-body-sm rounded-full border border-white/10 px-4 py-2 text-zinc-400 transition-colors hover:border-white/20 hover:text-zinc-200 disabled:opacity-50"
          >
            {removeBusy ? "Removing…" : "Remove"}
          </button>
        ) : null}
      </div>
    </div>
  )
}
